import React, { useEffect, useState } from "react";
import Slider, { SliderThumb } from "@mui/material/Slider";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { getDir } from "orion-components/i18n/Config/selector";
import { IconButton } from "@mui/material";
import { useSelector } from "react-redux";
import { Translate, getTranslation } from "orion-components/i18n";

const MINUTES_IN_HOUR = 60;
const HOURS_IN_DAY = 24;
//const DAYS_IN_WEEK = 7;

const sliderMarks = [
	{ value: 1, scaledValue: 20 / 60, label: "20s" }, // 20 sec
	{ value: 2, scaledValue: 30 / 60, label: "30s" }, // 20 sec
	{ value: 3, scaledValue: 1, label: "1m" }, // 1 min
	{ value: 4, scaledValue: 10, label: "10m" }, // 10 mins
	{ value: 5, scaledValue: 30, label: "30m" }, // 30 mins
	{ value: 6, scaledValue: 1 * MINUTES_IN_HOUR, label: "1h" }, // 1 hr
	{ value: 7, scaledValue: 2 * MINUTES_IN_HOUR, label: "2h" }, // 2 hrs
	{ value: 8, scaledValue: 4 * MINUTES_IN_HOUR, label: "4h" }, // 4 hrs
	{ value: 9, scaledValue: 6 * MINUTES_IN_HOUR, label: "6h" }, // 6 hrs
	{ value: 10, scaledValue: 12 * MINUTES_IN_HOUR, label: "12h" }, // 12 hrs
	{ value: 11, scaledValue: 1 * MINUTES_IN_HOUR * HOURS_IN_DAY, label: "1d" } // 1 day
];

const CustomSlider = styled(Slider)(() => ({
	color: "#4DB5F4",
	"& .MuiSlider-thumb": {
		height: 22,
		width: 22,
		color: "#fff",
		fontSize: "1em",
		backgroundColor: "#4DB5F4",
		"&:hover": {
			boxShadow: "0 0 0 8px rgba(58, 133, 137, 0.16)"
		},
		"& .airbnb-bar": {
			height: 9,
			width: 1,
			backgroundColor: "currentColor",
			marginLeft: 1,
			marginRight: 1
		}
	},
	"& .MuiSlider-rail": {
		color: "#437A9D"
	}
}));

const getScaledValue = (value) => {
	return sliderMarks.find((mark) => mark.value === value).scaledValue;
};

const formatLabel = (value) => {
	const sliderMark = sliderMarks.find((mark) => mark.value === value);
	return getTranslation(
		`global.CBComponents.newVideoPlayer.components.recorded.videoScalePicker.${sliderMark.label}`
	);
};

const ThumbComponent = (props) => {
	const { children, ...other } = props;
	return (
		<SliderThumb {...other} sx={{ color: "#fff", fontSize: 6 }}>
			{children}
			{formatLabel(children.props.value)}
		</SliderThumb>
	);
};

const VideoScalePicker = ({ duration, getScale }) => {
	const [value, setValue] = useState(1);

	const [displayMenu, setDisplayMenu] = useState(null);

	const handlePopoverClose = () => {
		setDisplayMenu(null);
	};
	const dir = useSelector((state) => getDir(state));

	useEffect(() => {
		// Set scale based on duration
		let scaleIndex = 0;
		while (sliderMarks[scaleIndex].scaledValue < duration && scaleIndex < sliderMarks.length - 1) {
			scaleIndex++;
		}
		if (sliderMarks[scaleIndex].scaledValue !== duration && scaleIndex > 0) {
			scaleIndex--;
		}
		setValue(sliderMarks[scaleIndex].value);
		getScale(sliderMarks[scaleIndex].scaledValue);
	}, [duration]);

	return (
		<div>
			<div style={{ width: 16.2, height: "100%" }}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
						fontSize: 10
					}}
				>
					<IconButton
						sx={{ fontSize: "1em", color: "#fff" }}
						onClick={() => {
							setDisplayMenu(true);
						}}
					>
						{formatLabel(value)}
					</IconButton>
				</div>
			</div>
			<div
				style={{
					display: displayMenu !== null ? "block" : "none",
					position: "absolute",
					bottom: 15,
					...(dir === "ltr" && { right: 15 }),
					...(dir === "rtl" && { left: 15 }),
					color: "#fff",
					width: 174,
					height: 35,
					background: "#1D1D1F",
					zIndex: "999"
				}}
				onMouseLeave={handlePopoverClose}
			>
				<div style={{ display: "flex", alignItems: "center", marginLeft: 18, marginRight: 18 }}>
					<div
						style={{
							flexBasis: "2.5%",
							display: "flex",
							alignItems: "center",
							marginRight: 4,
							fontSize: 9
						}}
					>
						<div style={{ width: 12, height: 9, fontSize: "1em" }}>
							<Translate value="global.CBComponents.newVideoPlayer.components.recorded.videoScalePicker.20s" />
						</div>
					</div>
					<div style={{ flexBasis: "95%", paddingTop: 2, paddingLeft: 8, paddingRight: 8 }}>
						<div style={{ width: 90, fontSize: 8 }}>
							<CustomSlider
								value={value}
								onChange={(event, newValue) => setValue(newValue)}
								onChangeCommitted={(event, newValue) => getScale(getScaledValue(newValue))}
								min={1}
								step={1}
								max={10}
								slots={{ thumb: ThumbComponent }}
							/>
						</div>
					</div>
					<div
						style={{
							flexBasis: "2.5%",
							display: "flex",
							alignItems: "center",
							...(dir === "ltr" && { marginLeft: 4 }),
							...(dir === "ltr" && { marginRight: 4 }),
							fontSize: 9
						}}
					>
						<div style={{ width: 12, height: 9, fontSize: "1em" }}>
							<Translate value="global.CBComponents.newVideoPlayer.components.recorded.videoScalePicker.1d" />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

VideoScalePicker.propTypes = {
	duration: PropTypes.number,
	getScale: PropTypes.func
};

export default VideoScalePicker;
